import { useEffect, useMemo, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { ClubCrest } from '../components/ClubCrest'
import { buildMatchSimulation } from './simulation'
import type { MatchOptions, MatchSummary, MatchSimulation } from './types'

interface WorldCupGroupTickerProps {
  options: MatchOptions
  onComplete: (summary: MatchSummary) => void
}

const GROUP_DURATION_MS = 7_000

function groupSeed(value: string): number {
  return [...value].reduce((total, character) => Math.imul(total ^ character.charCodeAt(0), 16777619) >>> 0, 2166136261)
}

function groupRandom(seed: number): () => number {
  let value = seed >>> 0
  return () => {
    value = Math.imul(value + 0x6D2B79F5, 1) >>> 0
    return value / 4294967296
  }
}

function selectGroupOpponents(options: MatchOptions): string[] {
  return [...new Set(options.opponents?.length ? options.opponents : [options.opponent])]
    .filter((name) => name && name !== options.homeTeam)
    .slice(0, 3)
}

function groupOutcomes(options: MatchOptions): Array<'win' | 'loss'> {
  const random = groupRandom(groupSeed(`${options.matchId}:${options.seed ?? ''}:world-cup-group`))
  const probability = Math.max(0, Math.min(1, options.winProbability ?? (options.expectedResult === 'win' ? .6 : .4)))
  return Array.from({ length: 3 }, () => random() < probability ? 'win' : 'loss')
}

/** Ticker exclusivo de los tres partidos del grupo. No comparte el flujo visual del tramo de liga. */
export function WorldCupGroupTicker({ options, onComplete }: WorldCupGroupTickerProps) {
  const fingerprint = `${options.matchId}-${options.expectedResult}-${(options.opponents ?? []).join('|')}-${options.seed ?? ''}`
  const fixtures = useMemo(() => {
    const opponents = selectGroupOpponents(options)
    const outcomes = groupOutcomes(options)
    return opponents.slice(0, 3).map((opponent, index): MatchSimulation => buildMatchSimulation({
      ...options,
      format: 'single',
      matchId: `${options.matchId}-grupo-${index + 1}`,
      title: 'Partido de grupos',
      opponent,
      expectedResult: outcomes[index]!,
      playerMinutes: 90,
    }))
  }, [fingerprint])
  const [completed, setCompleted] = useState(0)
  const [paused, setPaused] = useState(false)
  const timerRef = useRef<number | null>(null)
  const pausedRef = useRef(false)
  const elapsedRef = useRef(0)
  const fixtureCount = fixtures.length
  const finished = completed === fixtureCount
  const visibleFixtures = fixtures.slice(0, completed)
  const wins = fixtures.filter((fixture) => fixture.outcome === 'win').length
  const visibleWins = visibleFixtures.filter((fixture) => fixture.outcome === 'win').length
  const summary: MatchSummary = {
    matchId: options.matchId,
    opponent: options.opponent,
    homeGoals: fixtures.reduce((total, fixture) => total + fixture.homeGoals, 0),
    awayGoals: fixtures.reduce((total, fixture) => total + fixture.awayGoals, 0),
    playerGoals: fixtures.reduce((total, fixture) => total + fixture.playerGoals, 0),
    playerAssists: fixtures.reduce((total, fixture) => total + fixture.playerAssists, 0),
    outcome: wins >= 2 ? 'win' : 'loss',
    blockWins: wins,
    blockDraws: 0,
    blockPlayerWins: wins,
    blockOutcomes: fixtures.map((fixture) => fixture.outcome),
    blockFixtures: fixtures.map((fixture) => ({ ...fixture, playerMinutes: fixture.playerMinutes ?? 90 })),
    blockMinutes: fixtures.map(() => 90),
    blockAppearances: fixtures.length,
    fixturesPlayed: fixtures.length,
    blockOpponents: fixtures.map((fixture) => fixture.opponent),
    reputationGain: 0,
    blockOvrGain: 0,
    events: fixtures.flatMap((fixture) => fixture.events),
  }

  useEffect(() => {
    setCompleted(0)
    setPaused(false)
    pausedRef.current = false
    elapsedRef.current = 0
    let previousTick = performance.now()
    timerRef.current = window.setInterval(() => {
      const now = performance.now()
      if (!pausedRef.current) elapsedRef.current += now - previousTick
      previousTick = now
      if (pausedRef.current) return
      const nextCompleted = Math.min(fixtureCount, Math.floor(elapsedRef.current / GROUP_DURATION_MS * fixtureCount))
      setCompleted(nextCompleted)
      if (nextCompleted >= fixtureCount && timerRef.current !== null) {
        window.clearInterval(timerRef.current)
        timerRef.current = null
      }
    }, 100)
    return () => {
      if (timerRef.current !== null) window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [fixtures, fixtureCount])

  const togglePause = () => setPaused((current) => {
    pausedRef.current = !current
    return !current
  })
  const simulateResult = () => {
    if (timerRef.current !== null) window.clearInterval(timerRef.current)
    timerRef.current = null
    pausedRef.current = false
    setPaused(false)
    elapsedRef.current = GROUP_DURATION_MS
    setCompleted(fixtureCount)
  }

  return <section className="match-ticker world-cup-group" aria-label={`${options.title}: fase de grupos de ${options.homeTeam}`}>
    <header className="match-ticker__header">
      <div><p className="eyebrow">{options.title}</p><h2>Tres partidos. Necesitas dos victorias.</h2></div>
      <span className={`match-ticker__status${finished ? ' is-final' : ''}`}>{finished ? 'Final' : paused ? 'Pausa' : `${completed}/${fixtureCount}`}</span>
    </header>
    <div className="league-block__summary" aria-live="polite">
      <div className="league-block__club"><ClubCrest name={options.homeTeam} size="large" /><div><small>Tu selección</small><strong title={options.homeTeam}>{options.homeTeam}</strong></div></div>
      <div className="league-block__points"><strong>{visibleWins} <i>/</i> 2</strong><span>victorias · necesitas 2</span></div>
    </div>
    <div className="match-ticker__time" role="progressbar" aria-label="Partidos de grupos simulados" aria-valuemin={0} aria-valuemax={fixtureCount} aria-valuenow={completed} aria-valuetext={`${completed} de ${fixtureCount} partidos del grupo simulados`}><span style={{ transform: `scaleX(${completed / fixtureCount})` }} /></div>
    <section className="league-fixtures" aria-labelledby="world-cup-group-fixtures-title">
      <header><h3 id="world-cup-group-fixtures-title">Resultados del grupo</h3><span>{finished ? (summary.outcome === 'win' ? 'Clasificado' : 'Eliminado') : 'Simulando ×2,25'}</span></header>
      <ol>{fixtures.map((fixture, index) => {
        const isComplete = index < completed
        const resultLabel = fixture.outcome === 'win' ? 'Victoria' : 'Derrota'
        return <li key={fixture.matchId} className={`${isComplete ? `is-complete is-${fixture.outcome}` : ''}${index === completed && !finished ? ' is-live' : ''}`} aria-label={isComplete ? `${fixture.opponent}: ${resultLabel}, ${fixture.homeGoals} a ${fixture.awayGoals}` : undefined}>
          <ClubCrest name={fixture.opponent} size="small" /><strong title={fixture.opponent}>{fixture.opponent}</strong><span className="league-fixtures__minutes">{isComplete ? '90\'' : '—'}</span><span className="league-fixtures__production">{isComplete ? `${fixture.playerGoals} G · ${fixture.playerAssists} A` : '—'}</span><span className="league-fixtures__outcome">{isComplete ? resultLabel : ''}</span><span className="league-fixtures__score">{isComplete ? `${fixture.homeGoals} — ${fixture.awayGoals}` : 'Pendiente'}</span>
        </li>
      })}</ol>
    </section>
    <footer className="match-ticker__footer match-ticker__footer--controls-only">
      {finished ? <button className="button button--primary" type="button" onClick={() => onComplete(summary)}>Continuar</button> : <div className="match-ticker__controls"><button className="button match-ticker__pause" type="button" aria-pressed={paused} onClick={togglePause}><Icon icon={paused ? 'mdi:play' : 'mdi:pause'} /> {paused ? 'Reanudar' : 'Pausar'}</button><button className="button button--primary" type="button" onClick={simulateResult}><Icon icon="mdi:skip-forward" /> Simular hasta el resultado</button></div>}
    </footer>
  </section>
}
